import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useEventComments, useAddEventComment, useDeleteEventComment } from "@/hooks/useEventComments";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { notifyEventHost, notifyEventParticipants } from "@/hooks/useEventNotifications";

interface EventCommentsProps {
  eventId: string;
}

export const EventComments = ({ eventId }: EventCommentsProps) => {
  const { user } = useAuth();
  const { data: comments, isLoading } = useEventComments(eventId);
  const addComment = useAddEventComment();
  const deleteComment = useDeleteEventComment();
  const [commentText, setCommentText] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    addComment.mutate(
      { eventId, commentText: commentText.trim() },
      {
        onSuccess: async () => {
          setCommentText('');
          
          // Notify event host and participants about new comment
          await notifyEventHost(
            eventId,
            'event_comment',
            'New Event Comment',
            'Someone commented on your event.'
          );
          
          await notifyEventParticipants(
            eventId,
            'event_comment',
            'New Event Comment',
            'A new comment was added to an event you\'re attending.',
            user?.id
          );
        },
      }
    );
  };

  const handleDelete = (commentId: string) => {
    deleteComment.mutate({ commentId, eventId });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Comments ({comments?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Comment input */}
        {user && (
          <form onSubmit={handleSubmit} className="mb-6">
            <Textarea
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="mb-2"
              rows={3}
            />
            <Button
              type="submit"
              disabled={!commentText.trim() || addComment.isPending}
            >
              {addComment.isPending ? 'Posting...' : 'Post Comment'}
            </Button>
          </form>
        )}

        {/* Comments list */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : comments && comments.length > 0 ? (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="border rounded-lg p-4 space-y-2"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{comment.user_email}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {user && comment.user_id === user.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(comment.id)}
                      className="h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap">{comment.comment_text}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No comments yet. Be the first to comment!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
